import React from 'react';
import { Gift, Calendar, User, DollarSign, Sparkles, TrendingUp, Star, Target, Lightbulb } from 'lucide-react';

const CategoriesOverviewPage = ({ setCurrentPage }) => {
  const categoryTypes = [
    {
      id: 'occasion',
      title: 'Anledninger',
      icon: Calendar,
      color: 'primary',
      description: 'Finn gaver tilpasset spesielle øyeblikk og feiring',
      categories: [
        { name: 'Bryllupsgaver', emoji: '💍', page: 'wedding' },
        { name: 'Bursdagsgaver', emoji: '🎂', page: 'birthday' },
        { name: 'Julegaver', emoji: '🎄', page: 'christmas' },
        { name: 'Konfirmasjon', emoji: '✨', page: 'confirmation' },
        { name: 'Graduering', emoji: '🎓', page: 'graduation' },
        { name: 'Pensjonering', emoji: '🏆', page: 'retirement' }
      ]
    },
    {
      id: 'recipient',
      title: 'Mottakere',
      icon: User,
      color: 'accent',
      description: 'Gaver tilpasset hvem som skal motta dem',
      categories: [
        { name: 'Gaver til henne', emoji: '👩', page: 'for-her' },
        { name: 'Gaver til han', emoji: '👨', page: 'for-him' },
        { name: 'Gaver til barn', emoji: '👶', page: 'for-kids' },
        { name: 'Gaver til lærere', emoji: '👩‍🏫', page: 'for-teachers' },
        { name: 'Gaver til tenåringer', emoji: '🧒', page: 'for-teens' },
        { name: 'Gaver til besteforeldre', emoji: '👴', page: 'for-grandparents' }
      ]
    },
    {
      id: 'budget',
      title: 'Budsjett',
      icon: DollarSign,
      color: 'success',
      description: 'Finn perfekte gaver innenfor ditt budsjett',
      categories: [
        { name: 'Under 200 kr', emoji: '💸', page: 'under-200' },
        { name: '200-500 kr', emoji: '💎', page: '200-500' },
        { name: '500-1000 kr', emoji: '👑', page: '500-1000' },
        { name: 'Over 1000 kr', emoji: '💍', page: '1000-plus' },
        { name: 'Luksus-gaver', emoji: '✨', page: 'luxury' }
      ]
    },
    {
      id: 'category',
      title: 'Kategorier',
      icon: Sparkles,
      color: 'purple',
      description: 'Utforsk gaver etter type og interesser',
      categories: [
        { name: 'Elektronikk', emoji: '📱', page: 'electronics' },
        { name: 'Klær og mote', emoji: '👗', page: 'fashion' },
        { name: 'Hjem og interiør', emoji: '🏠', page: 'home' },
        { name: 'Sport og fritid', emoji: '⚽', page: 'sports' },
        { name: 'Bøker og kultur', emoji: '📚', page: 'books' },
        { name: 'Mat og drikke', emoji: '🍷', page: 'food' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-accent-50 to-purple-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">🎁</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gavekategorier</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Utforsk våre sorti kategorier for å finne den perfekte gaven. Enten du vet anledningen, 
              kjenner mottakeren eller har et bestemt budsjett - vi hjelper deg å finne veien.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-lg hover:from-primary-600 hover:to-accent-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Gå direkte til gavefinneren
            </button>
          </div>
        </div>
      </section>

      {/* Navigation Philosophy */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Tre måter å finne den perfekte gaven
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-lg p-6 border border-primary-100 text-center">
                <Calendar className="w-16 h-16 text-primary-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Start med anledningen</h3>
                <p className="text-secondary-600">
                  Vet du hva som skal feires? Start her for gaver som passer perfekt til anledningen - 
                  fra bryllup til jul til bursdager.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-purple-50 rounded-lg p-6 border border-accent-100 text-center">
                <User className="w-16 h-16 text-accent-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Tenk på mottakeren</h3>
                <p className="text-secondary-600">
                  Kjenner du personen godt? Utforsk gaver basert på hvem de er - alder, kjønn, 
                  relasjon til deg og deres unike personlighet.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100 text-center">
                <DollarSign className="w-16 h-16 text-purple-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Sett ditt budsjett</h3>
                <p className="text-secondary-600">
                  Har du et bestemt budsjett? Vi viser deg fantastiske gaver i alle prisklasser - 
                  fra gjennomtenkte småting til luksus-investeringer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Types */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Utforsk alle kategorier
            </h2>

            <div className="space-y-12">
              {categoryTypes.map((type, index) => (
                <div key={type.id} className="bg-white rounded-lg shadow-sm border border-secondary-100 overflow-hidden">
                  <div className={`bg-gradient-to-r from-${type.color}-500 to-${type.color}-600 p-6`}>
                    <div className="flex items-center justify-center text-center">
                      <type.icon className="w-12 h-12 text-white mr-4" />
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">{type.title}</h3>
                        <p className="text-white opacity-90">{type.description}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-8">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      {type.categories.map((category, catIndex) => (
                        <button
                          key={catIndex}
                          onClick={() => setCurrentPage(category.page)}
                          className="group bg-secondary-50 hover:bg-secondary-100 border border-secondary-200 hover:border-secondary-300 rounded-lg p-4 transition-all duration-200 hover:shadow-md"
                        >
                          <div className="text-center">
                            <div className="text-3xl mb-2 group-hover:scale-110 transition-transform duration-200">
                              {category.emoji}
                            </div>
                            <div className="text-sm font-medium text-secondary-800 group-hover:text-secondary-900">
                              {category.name}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Tips for å finne den perfekte gaven
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                  <Target className="w-8 h-8 text-success-500 mb-4" />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">Ikke sikker hvor du skal starte?</h3>
                  <p className="text-secondary-600 text-sm mb-4">
                    Prøv vår smarte gavefinner! Den stiller deg noen enkle spørsmål og gir personlige anbefalinger.
                  </p>
                  <button
                    onClick={() => setCurrentPage('gift-finder')}
                    className="text-success-600 hover:text-success-700 font-medium text-sm flex items-center"
                  >
                    <Gift className="w-4 h-4 mr-1" />
                    Start gavefinneren
                  </button>
                </div>

                <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                  <Lightbulb className="w-8 h-8 text-accent-500 mb-4" />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">Tenk utenfor boksen</h3>
                  <p className="text-secondary-600 text-sm">
                    De beste gavene er ofte de som viser at du kjenner personen godt. 
                    Observer deres interesser, utfordringer og drømmer.
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                  <Star className="w-8 h-8 text-purple-500 mb-4" />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">Timing er viktig</h3>
                  <p className="text-secondary-600 text-sm">
                    Noen gaver er perfekte for å gi spontant, mens andre krever planlegging. 
                    Tenk på når gaven vil bli brukt og verdsatt mest.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-warning-50 to-orange-50 rounded-lg p-6 border border-warning-100">
                  <TrendingUp className="w-8 h-8 text-warning-500 mb-4" />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">Kvalitet over kvantitet</h3>
                  <p className="text-secondary-600 text-sm">
                    En gjennomtenkt gave som viser omtanke er bedre enn mange tilfeldige ting. 
                    Fokuser på mening fremfor mengde.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-500 to-purple-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne den perfekte gaven?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Uansett hvilken kategori du velger, er vi her for å hjelpe deg å finne noe spesielt
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-primary-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('home')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-primary-600 transition-colors font-semibold"
              >
                Tilbake til forsiden
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CategoriesOverviewPage;